import React, { useEffect, useMemo, useState } from "react";
import {
  Loader2,
  Plus,
  Pencil,
  Trash2,
  ShoppingBag,
  CalendarDays,
  Link as LinkIcon,
  Package,
  X,
} from "lucide-react";

const EMPTY_FORM = {
  title: "",
  description: "",
  price: "",
  inventory: "",
  photos: [],
  variantsText: "",
  deliveryMethods: ["shipping"],
  associatedEvents: [],
  active: true,
};

function normalizeMerchResponse(data) {
  if (Array.isArray(data)) return data;
  if (Array.isArray(data.items)) return data.items;
  if (Array.isArray(data.merch)) return data.merch;
  return [];
}

export default function ManageMerch({ userEmail, events = [], onRefreshMerch }) {
  const [merchItems, setMerchItems] = useState([]);
  const [sellerEvents, setSellerEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [newImageFiles, setNewImageFiles] = useState([]);

  useEffect(() => {
    const fetchData = async () => {
      if (!userEmail) {
        setLoading(false);
        return;
      }

      try {
        const [merchRes, eventsRes] = await Promise.all([
          fetch(`/api/myMerch?email=${encodeURIComponent(userEmail)}`),
          fetch(`/api/myEvents?email=${encodeURIComponent(userEmail)}`),
        ]);

        const merchData = await merchRes.json();
        const eventsData = await eventsRes.json();

        setMerchItems(normalizeMerchResponse(merchData));
        setSellerEvents(eventsData?.events || events);
      } catch (error) {
        console.error(error);
        alert("Error loading merch dashboard.");
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [userEmail, events]);

  useEffect(() => {
    if (!showForm) return undefined;

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [showForm]);

  const eventMap = useMemo(() => {
    const source = sellerEvents.length ? sellerEvents : events;
    return source.reduce((acc, event) => {
      acc[event._id || event.id] = event;
      return acc;
    }, {});
  }, [sellerEvents, events]);

  const openCreateForm = () => {
    setEditingItem(null);
    setForm(EMPTY_FORM);
    setNewImageFiles([]);
    setShowForm(true);
  };

  const openEditForm = (item) => {
    setEditingItem(item);
    setForm({
      title: item.title || item.name || "",
      description: item.description || "",
      price: item.price ?? "",
      inventory: item.inventory ?? "",
      photos:
        Array.isArray(item.photos) && item.photos.length
          ? item.photos.filter(Boolean)
          : [item.imageUrl || item.image || ""].filter(Boolean),
      variantsText: Array.isArray(item.variants || item.sizes)
        ? (item.variants || item.sizes).join(", ")
        : "",
      deliveryMethods:
        Array.isArray(item.deliveryMethods) && item.deliveryMethods.length
          ? item.deliveryMethods
          : ["shipping"],
      associatedEvents: Array.isArray(item.associatedEvents)
        ? item.associatedEvents
        : item.eventId
        ? [item.eventId]
        : [],
      active: item.active !== false,
    });
    setNewImageFiles([]);
    setShowForm(true);
  };

  const resetForm = () => {
    setShowForm(false);
    setEditingItem(null);
    setForm(EMPTY_FORM);
    setNewImageFiles([]);
  };

  const updateFormField = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleImageUpload = (event) => {
    const files = Array.from(event.target.files || []);
    if (!files.length) return;

    setNewImageFiles((prev) => [...prev, ...files]);
    event.target.value = "";
  };

  const removeExistingPhoto = (index) => {
    setForm((prev) => ({
      ...prev,
      photos: prev.photos.filter((_, photoIndex) => photoIndex !== index),
    }));
  };

  const removeNewImage = (index) => {
    setNewImageFiles((prev) => prev.filter((_, fileIndex) => fileIndex !== index));
  };

  const toggleDeliveryMethod = (method) => {
    setForm((prev) => {
      const exists = prev.deliveryMethods.includes(method);
      const deliveryMethods = exists
        ? prev.deliveryMethods.filter((entry) => entry !== method)
        : [...prev.deliveryMethods, method];

      return {
        ...prev,
        deliveryMethods: deliveryMethods.length ? deliveryMethods : ["shipping"],
      };
    });
  };

  const toggleAssociatedEvent = (eventId) => {
    setForm((prev) => ({
      ...prev,
      associatedEvents: prev.associatedEvents.includes(eventId)
        ? prev.associatedEvents.filter((entry) => entry !== eventId)
        : [...prev.associatedEvents, eventId],
    }));
  };

  const handleSave = async (e) => {
    e.preventDefault();

    if (!form.title || !form.price) {
      alert("Please add at least a title and a price.");
      return;
    }

    setSaving(true);

    const payload = new FormData();
    payload.append("sellerEmail", userEmail);
    payload.append("title", form.title);
    payload.append("description", form.description);
    payload.append("price", String(Number(form.price)));
    payload.append("inventory", String(Number(form.inventory || 0)));
    payload.append("photos", JSON.stringify(form.photos.filter(Boolean)));
    payload.append(
      "variants",
      JSON.stringify(
        form.variantsText
          .split(",")
          .map((entry) => entry.trim())
          .filter(Boolean)
      )
    );
    payload.append("deliveryMethods", JSON.stringify(form.deliveryMethods));
    payload.append("associatedEvents", JSON.stringify(form.associatedEvents));
    payload.append("active", String(form.active));
    newImageFiles.forEach((file) => payload.append("images", file));

    try {
      const endpoint = editingItem
        ? `/api/updateMerch/${editingItem._id || editingItem.id}`
        : "/api/createMerch";
      const method = editingItem ? "PUT" : "POST";

      const res = await fetch(endpoint, {
        method,
        body: payload,
      });

      const data = await res.json();

      if (!res.ok || data.success === false) {
        throw new Error(data.message || "Failed to save merch item.");
      }

      const savedItem = data.item || data.merch || data;

      setMerchItems((prev) => {
        if (editingItem) {
          return prev.map((item) =>
            (item._id || item.id) === (editingItem._id || editingItem.id)
              ? savedItem
              : item
          );
        }
        return [savedItem, ...prev];
      });

      resetForm();
      if (onRefreshMerch) {
        onRefreshMerch();
      }
    } catch (error) {
      console.error(error);
      alert(error.message || "Failed to save merch item.");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (item) => {
    if (!window.confirm(`Delete ${item.title || item.name}?`)) {
      return;
    }

    try {
      const res = await fetch(`/api/deleteMerch/${item._id || item.id}`, {
        method: "DELETE",
      });
      const data = await res.json();

      if (!res.ok || data.success === false) {
        throw new Error(data.message || "Failed to delete merch item.");
      }

      setMerchItems((prev) =>
        prev.filter((entry) => (entry._id || entry.id) !== (item._id || item.id))
      );

      if (onRefreshMerch) {
        onRefreshMerch();
      }
    } catch (error) {
      console.error(error);
      alert(error.message || "Failed to delete merch item.");
    }
  };

  const getLinkedEventNames = (item) => {
    const ids = Array.isArray(item.associatedEvents)
      ? item.associatedEvents
      : item.eventId
      ? [item.eventId]
      : [];

    return ids.map((id) => eventMap[id]?.name).filter(Boolean);
  };

  if (loading) {
    return (
      <main className="min-h-screen bg-slate-50">
        <div className="mx-auto max-w-7xl px-4 py-12 sm:px-6 lg:px-8">
          <div className="rounded-[2rem] border border-slate-200 bg-white p-10 shadow-sm">
            <div className="flex items-center gap-3 text-slate-600">
              <Loader2 className="h-5 w-5 animate-spin" />
              <span className="text-sm font-medium">Loading your merch...</span>
            </div>
          </div>
        </div>
      </main>
    );
  }

  return (
    <main className="min-h-screen bg-slate-50">
      <div className="mx-auto max-w-7xl px-4 py-10 sm:px-6 lg:px-8">
        <header className="mb-8 flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <h1 className="text-3xl font-semibold tracking-tight text-slate-900 sm:text-4xl">
              Merch Management
            </h1>
            <p className="mt-2 max-w-2xl text-base text-slate-600">
              Create standalone merch listings, then optionally attach them to events so they can appear alongside tickets.
            </p>
          </div>

          <button
            onClick={openCreateForm}
            className="inline-flex items-center justify-center gap-2 rounded-2xl bg-slate-900 px-5 py-3 text-sm font-semibold text-white transition hover:bg-slate-800"
          >
            <Plus className="h-4 w-4" />
            Add merch item
          </button>
        </header>

        {merchItems.length === 0 ? (
          <div className="rounded-[2rem] border border-slate-200 bg-white px-8 py-16 text-center shadow-sm">
            <h2 className="text-2xl font-semibold tracking-tight text-slate-900">
              No merch yet
            </h2>
            <p className="mx-auto mt-3 max-w-md text-sm leading-6 text-slate-500">
              Build out your merch catalog here, then associate items with any upcoming shows when you want them to show up next to ticket sales.
            </p>
            <button
              onClick={openCreateForm}
              className="mt-8 inline-flex items-center gap-2 rounded-full bg-slate-900 px-5 py-3 text-sm font-medium text-white transition hover:bg-slate-800"
            >
              <Plus className="h-4 w-4" />
              Create your first merch item
            </button>
          </div>
        ) : (
          <div className="grid gap-6 md:grid-cols-2 xl:grid-cols-3">
            {merchItems.map((item) => {
              const linkedEvents = getLinkedEventNames(item);
              const photo =
                Array.isArray(item.photos) && item.photos.length
                  ? item.photos[0]
                  : item.imageUrl ||
                    item.image ||
                    "https://images.unsplash.com/photo-1523381210434-271e8be1f52b?auto=format&fit=crop&w=1200&q=80";

              return (
                <article
                  key={item._id || item.id}
                  className="overflow-hidden rounded-[2rem] border border-slate-200 bg-white shadow-sm"
                >
                  <div className="relative h-64 bg-slate-100">
                    <img
                      src={photo}
                      alt={item.title || item.name}
                      className="h-full w-full object-cover"
                    />
                    <div className="absolute left-4 top-4 flex flex-wrap gap-2">
                      <span className="rounded-full bg-white/90 px-3 py-1 text-xs font-semibold text-slate-800 shadow-sm backdrop-blur-sm">
                        ${(Number(item.price) || 0).toFixed(2)}
                      </span>
                      <span
                        className={`rounded-full px-3 py-1 text-xs font-semibold shadow-sm backdrop-blur-sm ${
                          item.active === false
                            ? "bg-red-600 text-white"
                            : "bg-emerald-50 text-emerald-700"
                        }`}
                      >
                        {item.active === false ? "Inactive" : "Active"}
                      </span>
                    </div>
                  </div>

                  <div className="space-y-4 p-6">
                    <div>
                      <h2 className="text-xl font-semibold tracking-tight text-slate-900">
                        {item.title || item.name}
                      </h2>
                      <p className="mt-2 line-clamp-3 text-sm leading-6 text-slate-600">
                        {item.description || "No description yet."}
                      </p>
                    </div>

                    <div className="grid gap-3">
                      <div className="flex items-center gap-3 rounded-2xl bg-slate-50 px-4 py-3 text-sm text-slate-600">
                        <Package className="h-4 w-4 text-slate-400" />
                        <span>{Number(item.inventory || 0)} in inventory</span>
                      </div>

                      <div className="flex items-start gap-3 rounded-2xl bg-slate-50 px-4 py-3 text-sm text-slate-600">
                        <LinkIcon className="mt-0.5 h-4 w-4 text-slate-400" />
                        <span>
                          {linkedEvents.length > 0
                            ? linkedEvents.join(" • ")
                            : "Not linked to any events yet"}
                        </span>
                      </div>

                      <div className="flex items-center gap-3 rounded-2xl bg-slate-50 px-4 py-3 text-sm text-slate-600">
                        <CalendarDays className="h-4 w-4 text-slate-400" />
                        <span>
                          {(Array.isArray(item.associatedEvents)
                            ? item.associatedEvents.length
                            : item.eventId
                            ? 1
                            : 0)}{" "}
                          linked event
                          {(Array.isArray(item.associatedEvents)
                            ? item.associatedEvents.length
                            : item.eventId
                            ? 1
                            : 0) === 1
                            ? ""
                            : "s"}
                        </span>
                      </div>
                    </div>

                    <div className="grid gap-3 sm:grid-cols-2">
                      <button
                        onClick={() => openEditForm(item)}
                        className="inline-flex items-center justify-center gap-2 rounded-2xl bg-amber-500 px-4 py-3 text-sm font-semibold text-white transition hover:bg-amber-600"
                      >
                        <Pencil className="h-4 w-4" />
                        Edit
                      </button>
                      <button
                        onClick={() => handleDelete(item)}
                        className="inline-flex items-center justify-center gap-2 rounded-2xl bg-red-600 px-4 py-3 text-sm font-semibold text-white transition hover:bg-red-700"
                      >
                        <Trash2 className="h-4 w-4" />
                        Delete
                      </button>
                    </div>
                  </div>
                </article>
              );
            })}
          </div>
        )}
      </div>

      {showForm && (
        <div className="fixed inset-0 z-50 bg-slate-950/60 backdrop-blur-sm">
          <div className="flex h-screen items-center justify-center p-3 sm:p-6">
            <div className="flex max-h-[92vh] w-full max-w-4xl flex-col overflow-hidden rounded-[2rem] border border-slate-200 bg-white shadow-2xl">
              <div className="flex items-start justify-between border-b border-slate-200 px-6 py-5 sm:px-8">
                <div>
                  <h3 className="text-2xl font-semibold tracking-tight text-slate-900">
                    {editingItem ? "Edit merch item" : "Create merch item"}
                  </h3>
                  <p className="mt-1 text-sm text-slate-500">
                    Merch can live on its own storefront page and be linked to one or more events.
                  </p>
                </div>
                <button
                  onClick={resetForm}
                  className="rounded-full p-2 text-slate-400 transition hover:bg-slate-100 hover:text-slate-700"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>

              <form
                onSubmit={handleSave}
                className="flex-1 overflow-y-auto px-5 py-5 sm:px-8 sm:py-6"
              >
                <div className="space-y-6 pb-2">
                  <div className="grid gap-5 sm:grid-cols-2">
                    <div className="sm:col-span-2">
                      <label className="mb-2 block text-sm font-medium text-slate-700">
                        Title
                      </label>
                      <input
                        value={form.title}
                        onChange={(e) => updateFormField("title", e.target.value)}
                        className="w-full rounded-2xl border border-slate-200 px-4 py-3 outline-none transition focus:border-slate-400 focus:ring-4 focus:ring-slate-100"
                        placeholder="Tour shirt, signed poster, hat..."
                      />
                    </div>

                    <div className="sm:col-span-2">
                      <label className="mb-2 block text-sm font-medium text-slate-700">
                        Description
                      </label>
                      <textarea
                        value={form.description}
                        onChange={(e) =>
                          updateFormField("description", e.target.value)
                        }
                        rows="4"
                        className="w-full rounded-2xl border border-slate-200 px-4 py-3 outline-none transition focus:border-slate-400 focus:ring-4 focus:ring-slate-100"
                        placeholder="Describe the item, what buyers receive, and any pickup / shipping notes."
                      />
                    </div>

                    <div>
                      <label className="mb-2 block text-sm font-medium text-slate-700">
                        Price
                      </label>
                      <input
                        type="number"
                        min="0"
                        step="0.01"
                        value={form.price}
                        onChange={(e) => updateFormField("price", e.target.value)}
                        className="w-full rounded-2xl border border-slate-200 px-4 py-3 outline-none transition focus:border-slate-400 focus:ring-4 focus:ring-slate-100"
                      />
                    </div>

                    <div>
                      <label className="mb-2 block text-sm font-medium text-slate-700">
                        Inventory
                      </label>
                      <input
                        type="number"
                        min="0"
                        value={form.inventory}
                        onChange={(e) =>
                          updateFormField("inventory", e.target.value)
                        }
                        className="w-full rounded-2xl border border-slate-200 px-4 py-3 outline-none transition focus:border-slate-400 focus:ring-4 focus:ring-slate-100"
                      />
                    </div>

                    <div className="sm:col-span-2">
                      <label className="mb-2 block text-sm font-medium text-slate-700">
                        Variants
                      </label>
                      <input
                        value={form.variantsText}
                        onChange={(e) =>
                          updateFormField("variantsText", e.target.value)
                        }
                        className="w-full rounded-2xl border border-slate-200 px-4 py-3 outline-none transition focus:border-slate-400 focus:ring-4 focus:ring-slate-100"
                        placeholder="S, M, L, XL or Red, Blue"
                      />
                    </div>
                  </div>

                  <div className="rounded-[1.75rem] border border-slate-200 bg-slate-50/70 p-4 sm:p-5">
                    <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                      <div>
                        <label className="block text-sm font-semibold text-slate-800">
                          Product images
                        </label>
                        <p className="mt-1 text-sm text-slate-500">
                          Upload up to 8 images. The first image becomes the cover photo.
                        </p>
                      </div>
                      <label className="inline-flex cursor-pointer items-center justify-center rounded-2xl bg-slate-900 px-4 py-3 text-sm font-semibold text-white transition hover:bg-slate-800">
                        Add images
                        <input
                          type="file"
                          accept="image/*"
                          multiple
                          className="hidden"
                          onChange={handleImageUpload}
                        />
                      </label>
                    </div>

                    {form.photos.length === 0 && newImageFiles.length === 0 ? (
                      <div className="mt-4 rounded-2xl border border-dashed border-slate-300 bg-white px-4 py-8 text-center text-sm text-slate-500">
                        No images uploaded yet. Add a few product shots so buyers can preview the item.
                      </div>
                    ) : (
                      <div className="mt-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                        {form.photos.map((photo, index) => (
                          <div
                            key={`existing-${index}`}
                            className="overflow-hidden rounded-[1.5rem] border border-slate-200 bg-white shadow-sm"
                          >
                            <div className="relative aspect-[4/3] bg-slate-100">
                              <img
                                src={photo}
                                alt={`Existing upload ${index + 1}`}
                                className="h-full w-full object-cover"
                              />
                              {index === 0 && (
                                <span className="absolute left-3 top-3 rounded-full bg-white/90 px-3 py-1 text-[11px] font-semibold text-slate-800 shadow-sm">
                                  Cover
                                </span>
                              )}
                            </div>
                            <div className="flex items-center justify-between gap-3 p-3">
                              <span className="truncate text-xs font-medium text-slate-500">
                                Existing image
                              </span>
                              <button
                                type="button"
                                onClick={() => removeExistingPhoto(index)}
                                className="rounded-xl bg-slate-100 px-3 py-2 text-xs font-semibold text-slate-700 transition hover:bg-slate-200"
                              >
                                Remove
                              </button>
                            </div>
                          </div>
                        ))}

                        {newImageFiles.map((file, index) => (
                          <div
                            key={`${file.name}-${index}`}
                            className="overflow-hidden rounded-[1.5rem] border border-emerald-200 bg-white shadow-sm"
                          >
                            <div className="relative aspect-[4/3] bg-slate-100">
                              <img
                                src={URL.createObjectURL(file)}
                                alt={file.name}
                                className="h-full w-full object-cover"
                              />
                              {form.photos.length === 0 && index === 0 && (
                                <span className="absolute left-3 top-3 rounded-full bg-white/90 px-3 py-1 text-[11px] font-semibold text-slate-800 shadow-sm">
                                  Cover
                                </span>
                              )}
                              <span className="absolute right-3 top-3 rounded-full bg-emerald-600 px-3 py-1 text-[11px] font-semibold text-white shadow-sm">
                                New
                              </span>
                            </div>
                            <div className="flex items-center justify-between gap-3 p-3">
                              <span className="truncate text-xs font-medium text-slate-500">
                                {file.name}
                              </span>
                              <button
                                type="button"
                                onClick={() => removeNewImage(index)}
                                className="rounded-xl bg-slate-100 px-3 py-2 text-xs font-semibold text-slate-700 transition hover:bg-slate-200"
                              >
                                Remove
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  <div className="grid gap-6 lg:grid-cols-2">
                    <div>
                      <p className="mb-3 text-sm font-medium text-slate-700">
                        Delivery methods
                      </p>
                      <div className="grid gap-3 sm:grid-cols-2">
                        {["shipping", "pickup"].map((method) => (
                          <button
                            key={method}
                            type="button"
                            onClick={() => toggleDeliveryMethod(method)}
                            className={`rounded-2xl border px-4 py-3 text-sm font-medium transition ${
                              form.deliveryMethods.includes(method)
                                ? "border-slate-900 bg-slate-900 text-white"
                                : "border-slate-200 bg-white text-slate-700 hover:bg-slate-50"
                            }`}
                          >
                            {method === "shipping" ? "Shipping" : "Pickup / handoff"}
                          </button>
                        ))}
                      </div>
                    </div>

                    <div>
                      <p className="mb-3 text-sm font-medium text-slate-700">
                        Listing status
                      </p>
                      <button
                        type="button"
                        onClick={() => updateFormField("active", !form.active)}
                        className={`w-full rounded-2xl border px-4 py-3 text-sm font-medium transition ${
                          form.active
                            ? "border-emerald-200 bg-emerald-50 text-emerald-700"
                            : "border-slate-200 bg-slate-100 text-slate-600"
                        }`}
                      >
                        {form.active ? "Active and visible" : "Inactive / hidden"}
                      </button>
                    </div>
                  </div>

                  <div>
                    <p className="mb-3 text-sm font-medium text-slate-700">
                      Associate with events
                    </p>
                    {Object.values(eventMap).length === 0 ? (
                      <div className="rounded-2xl bg-slate-50 px-4 py-3 text-sm text-slate-500">
                        You have no events yet. This merch will still appear in the merch storefront.
                      </div>
                    ) : (
                      <div className="grid gap-3 sm:grid-cols-2">
                        {Object.values(eventMap).map((event) => {
                          const eventId = event._id || event.id;
                          const isSelected = form.associatedEvents.includes(eventId);

                          return (
                            <button
                              key={eventId}
                              type="button"
                              onClick={() => toggleAssociatedEvent(eventId)}
                              className={`rounded-2xl border px-4 py-3 text-left text-sm transition ${
                                isSelected
                                  ? "border-slate-900 bg-slate-900 text-white"
                                  : "border-slate-200 bg-white text-slate-700 hover:bg-slate-50"
                              }`}
                            >
                              <div className="font-semibold">{event.name}</div>
                              <div
                                className={`mt-1 text-xs ${
                                  isSelected ? "text-white/80" : "text-slate-500"
                                }`}
                              >
                                {event.location}
                              </div>
                            </button>
                          );
                        })}
                      </div>
                    )}
                  </div>
                </div>

                <div className="flex flex-col-reverse gap-3 border-t border-slate-200 bg-white px-5 py-4 sm:flex-row sm:justify-end sm:px-8">
                  <button
                    type="button"
                    onClick={resetForm}
                    className="rounded-2xl bg-slate-100 px-5 py-3 text-sm font-medium text-slate-700 transition hover:bg-slate-200"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={saving}
                    className="inline-flex items-center justify-center gap-2 rounded-2xl bg-emerald-600 px-5 py-3 text-sm font-semibold text-white transition hover:bg-emerald-700 disabled:cursor-not-allowed disabled:bg-emerald-300"
                  >
                    {saving ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <ShoppingBag className="h-4 w-4" />
                    )}
                    {editingItem ? "Save changes" : "Create merch item"}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </main>
  );
}